import { useQuery } from "@tanstack/react-query";
import { Check, LoaderCircle, MapPin, Search } from "lucide-react";
import { useEffect, useId, useState } from "react";
import { api } from "../lib/api";
import type { PlaceSuggestion } from "../lib/types";

type Props = {
  value: PlaceSuggestion | null;
  initialText?: string;
  context: string;
  latitude: string | null;
  longitude: string | null;
  placeholder?: string;
  inputLabel?: string;
  searchLabel?: string;
  confirmationLabel?: string;
  onSelect: (place: PlaceSuggestion | null) => void;
};

function isGoogleMapsLink(value: string) {
  try {
    const parsed = new URL(value);
    return parsed.protocol === "https:" && ["maps.app.goo.gl", "maps.google.com", "www.google.com", "google.com"].includes(parsed.hostname) && (parsed.hostname === "maps.app.goo.gl" || parsed.pathname.startsWith("/maps"));
  } catch {
    return false;
  }
}

export function PlaceCombobox({
  value,
  initialText = "",
  context,
  latitude,
  longitude,
  placeholder = "Search an actual place nearby",
  inputLabel = "Actual place or meeting point",
  searchLabel = "real places",
  confirmationLabel = "Mapped place",
  onSelect,
}: Props) {
  const listId = useId();
  const [query, setQuery] = useState(value?.display_name || initialText);
  const [debounced, setDebounced] = useState(query);
  const [lastSearchName, setLastSearchName] = useState(value?.name || initialText);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(query.trim()), 500);
    return () => window.clearTimeout(timer);
  }, [query]);

  const selectedText = value?.display_name || "";
  const enabled = debounced.length >= 2 && debounced !== selectedText;
  const resolvingMapLink = isGoogleMapsLink(debounced);
  const { data: suggestions = [], isFetching, isError } = useQuery({
    queryKey: ["actual-place-search", debounced, context, latitude, longitude, lastSearchName],
    queryFn: async ({ signal }) => {
      if (resolvingMapLink) {
        const params = new URLSearchParams({ url: debounced });
        if (lastSearchName && !isGoogleMapsLink(lastSearchName)) params.set("name", lastSearchName);
        return [await api<PlaceSuggestion>(`/places/resolve-map-link?${params}`, { signal })];
      }
      const params = new URLSearchParams({ q: debounced, context, limit: "8" });
      if (latitude && longitude) {
        params.set("lat", latitude);
        params.set("lon", longitude);
      }
      return api<PlaceSuggestion[]>(`/places/search?${params}`, { signal });
    },
    enabled,
    staleTime: 10 * 60 * 1000,
    retry: 0,
  });

  const choose = (place: PlaceSuggestion) => {
    setQuery(place.display_name);
    setDebounced(place.display_name);
    setOpen(false);
    setActiveIndex(-1);
    onSelect(place);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") return setOpen(false);
    if (!suggestions.length) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => (index + 1) % suggestions.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => index <= 0 ? suggestions.length - 1 : index - 1);
    } else if (event.key === "Enter" && activeIndex >= 0) {
      event.preventDefault();
      choose(suggestions[activeIndex]);
    }
  };

  return <div className="location-field place-search-field">
    <div className={`location-input${value ? " verified" : ""}`}>
      <Search size={17} />
      <input
        value={query}
        autoComplete="off"
        placeholder={placeholder}
        aria-label={inputLabel}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open && enabled}
        aria-controls={listId}
        aria-activedescendant={activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
        onFocus={() => setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 150)}
        onKeyDown={handleKeyDown}
        onChange={(event) => {
          const nextValue = event.target.value;
          setQuery(nextValue);
          if (!isGoogleMapsLink(nextValue)) setLastSearchName(nextValue);
          setOpen(true);
          setActiveIndex(-1);
          onSelect(null);
        }}
      />
      {isFetching ? <LoaderCircle className="location-spinner" size={17} /> : value ? <Check className="location-check" size={17} /> : null}
    </div>
    {open && enabled && <div className="location-results" id={listId} role="listbox">
      {isFetching && !suggestions.length ? <div className="location-message"><LoaderCircle className="location-spinner" size={16} />Finding {searchLabel} around {context}…</div> : suggestions.map((place, index) => <button
        type="button"
        role="option"
        aria-selected={index === activeIndex}
        id={`${listId}-${index}`}
        className={index === activeIndex ? "active" : ""}
        key={place.provider_id}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => choose(place)}
      ><span className="suggestion-photo">{place.image_url ? <img src={place.image_url} alt={`View of ${place.name}`} loading="lazy" /> : <MapPin size={18} />}</span><span><strong>{place.name}</strong><small>{place.display_name}</small></span><i>{place.provider_id.startsWith("google:") ? "Google Places" : place.category || "Mapped place"}</i></button>)}
      {!isFetching && !resolvingMapLink && !suggestions.length && <div className="location-message map-search-fallback"><span>{isError ? "The hotel directory is temporarily unavailable." : "This property is not in the open hotel directory yet."}</span><a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(debounced)}`} target="_blank" rel="noreferrer">Find hotel on Google Maps ↗</a><small>Open the hotel, choose Share → Copy link, then paste that link here. Rihvo.fun will pin it automatically.</small></div>}
      {isError && resolvingMapLink && <div className="location-message error">That Google Maps link did not contain an exact place. Open the hotel itself, choose Share → Copy link, and paste that link.</div>}
    </div>}
    {value && <div className="location-confirmation"><Check size={13} /><span><strong>{confirmationLabel}</strong>{value.name} · coordinates saved{value.provider_id.startsWith("google:") ? " via Google Places" : ""}</span></div>}
  </div>;
}
